/**
 * Point class for 2D coordinate operations
 *
 * Represents a point in 2D space with x and y coordinates.
 * Provides various mathematical operations for point manipulation.
 *
 * This class is SSR-compatible and doesn't use any browser-specific APIs.
 *
 * @example
 * ```typescript
 * const p1 = new Point(10, 20);
 * const p2 = new Point(30, 40);
 * const sum = p1.add(p2); // Point(40, 60)
 * const distance = p1.distanceFrom(p2);
 * ```
 */

export interface XY {
  x: number;
  y: number;
}

export class Point implements XY {
  x: number;
  y: number;

  constructor();
  constructor(x: number, y: number);
  constructor(point: XY);
  constructor(arg0: number | XY = 0, y = 0) {
    if (typeof arg0 === 'object') {
      this.x = arg0.x;
      this.y = arg0.y;
    } else {
      this.x = arg0;
      this.y = y;
    }
  }

  /**
   * Adds another point to this one and returns a new point
   * @param that - Point to add
   * @returns New Point instance with added values
   */
  add(that: XY): Point {
    return new Point(this.x + that.x, this.y + that.y);
  }

  /**
   * Adds a scalar value to both x and y coordinates
   * @param scalar - Value to add
   * @returns New Point with added value
   */
  scalarAdd(scalar: number): Point {
    return new Point(this.x + scalar, this.y + scalar);
  }

  /**
   * Subtracts another point from this point and returns a new one
   * @param that - Point to subtract
   * @returns New Point with subtracted values
   */
  subtract(that: XY): Point {
    return new Point(this.x - that.x, this.y - that.y);
  }

  /**
   * Subtracts a scalar value from both x and y coordinates
   * @param scalar - Value to subtract
   * @returns New Point with subtracted value
   */
  scalarSubtract(scalar: number): Point {
    return new Point(this.x - scalar, this.y - scalar);
  }

  /**
   * Multiplies this point by another point and returns a new one
   * @param that - Point to multiply with
   * @returns New Point with multiplied values
   */
  multiply(that: XY): Point {
    return new Point(this.x * that.x, this.y * that.y);
  }

  /**
   * Multiplies this point by a scalar value
   * @param scalar - Value to multiply with
   * @returns New Point with multiplied values
   */
  scalarMultiply(scalar: number): Point {
    return new Point(this.x * scalar, this.y * scalar);
  }

  /**
   * Divides this point by another point
   * @param that - Point to divide by
   * @returns New Point with divided values
   */
  divide(that: XY): Point {
    return new Point(this.x / that.x, this.y / that.y);
  }

  /**
   * Divides this point by a scalar value
   * @param scalar - Value to divide by
   * @returns New Point with divided values
   */
  scalarDivide(scalar: number): Point {
    return new Point(this.x / scalar, this.y / scalar);
  }

  /**
   * Checks if this point is equal to another one
   * @param that - Point to compare with
   * @returns True if points are equal
   */
  eq(that: XY): boolean {
    return this.x === that.x && this.y === that.y;
  }

  /**
   * Checks if this point is less than another one
   * @param that - Point to compare with
   * @returns True if both x and y are less
   */
  lt(that: XY): boolean {
    return this.x < that.x && this.y < that.y;
  }

  /**
   * Checks if this point is less than or equal to another one
   * @param that - Point to compare with
   * @returns True if both x and y are less than or equal
   */
  lte(that: XY): boolean {
    return this.x <= that.x && this.y <= that.y;
  }

  /**
   * Checks if this point is greater than another one
   * @param that - Point to compare with
   * @returns True if both x and y are greater
   */
  gt(that: XY): boolean {
    return this.x > that.x && this.y > that.y;
  }

  /**
   * Checks if this point is greater than or equal to another one
   * @param that - Point to compare with
   * @returns True if both x and y are greater than or equal
   */
  gte(that: XY): boolean {
    return this.x >= that.x && this.y >= that.y;
  }

  /**
   * Linear interpolation between this point and another one
   * @param that - Target point
   * @param t - Interpolation factor (0 to 1), default 0.5
   * @returns New interpolated Point
   */
  lerp(that: XY, t = 0.5): Point {
    t = Math.max(Math.min(1, t), 0);
    return new Point(this.x + (that.x - this.x) * t, this.y + (that.y - this.y) * t);
  }

  /**
   * Calculates distance from this point to another one
   * @param that - Target point
   * @returns Distance between points
   */
  distanceFrom(that: XY): number {
    const dx = this.x - that.x;
    const dy = this.y - that.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Returns the midpoint between this point and another one
   * @param that - Other point
   * @returns Midpoint
   */
  midPointFrom(that: XY): Point {
    return this.lerp(that, 0.5);
  }

  /**
   * Returns a new point which is the minimum of this and another one
   * @param that - Point to compare with
   * @returns Point with minimum x and y values
   */
  min(that: XY): Point {
    return new Point(Math.min(this.x, that.x), Math.min(this.y, that.y));
  }

  /**
   * Returns a new point which is the maximum of this and another one
   * @param that - Point to compare with
   * @returns Point with maximum x and y values
   */
  max(that: XY): Point {
    return new Point(Math.max(this.x, that.x), Math.max(this.y, that.y));
  }

  /**
   * Returns string representation of this point
   * @returns String in format "x,y"
   */
  toString(): string {
    return `${this.x},${this.y}`;
  }

  /**
   * Sets x and y coordinates of this point
   * @param x - X coordinate
   * @param y - Y coordinate
   * @returns This point for chaining
   */
  setXY(x: number, y: number): this {
    this.x = x;
    this.y = y;
    return this;
  }

  /**
   * Sets x coordinate of this point
   * @param x - X coordinate
   * @returns This point for chaining
   */
  setX(x: number): this {
    this.x = x;
    return this;
  }

  /**
   * Sets y coordinate of this point
   * @param y - Y coordinate
   * @returns This point for chaining
   */
  setY(y: number): this {
    this.y = y;
    return this;
  }

  /**
   * Sets coordinates from another point
   * @param that - Source point
   * @returns This point for chaining
   */
  setFromPoint(that: XY): this {
    this.x = that.x;
    this.y = that.y;
    return this;
  }

  /**
   * Swaps coordinates with another point
   * @param that - Point to swap with
   */
  swap(that: XY): void {
    const x = this.x;
    const y = this.y;
    this.x = that.x;
    this.y = that.y;
    that.x = x;
    that.y = y;
  }

  /**
   * Creates a clone of this point
   * @returns New Point with same coordinates
   */
  clone(): Point {
    return new Point(this.x, this.y);
  }

  /**
   * Rotates point around origin by given radians
   * @param radians - Rotation angle in radians
   * @param origin - Point to rotate around (default: ZERO)
   * @returns New rotated point
   */
  rotate(radians: number, origin: XY = ZERO): Point {
    const sin = Math.sin(radians);
    const cos = Math.cos(radians);
    const p = this.subtract(origin);
    const rotated = new Point(p.x * cos - p.y * sin, p.x * sin + p.y * cos);
    return rotated.add(origin);
  }

  /**
   * Applies transform matrix to point
   * @param t - Transform matrix [a, b, c, d, e, f]
   * @param ignoreOffset - If true, doesn't apply translation (e, f)
   * @returns Transformed point
   */
  transform(t: [number, number, number, number, number, number], ignoreOffset = false): Point {
    return new Point(
      t[0] * this.x + t[2] * this.y + (ignoreOffset ? 0 : t[4]),
      t[1] * this.x + t[3] * this.y + (ignoreOffset ? 0 : t[5])
    );
  }
}

/**
 * Constant point at origin (0, 0)
 */
export const ZERO = new Point(0, 0);
